// apps/patient/src/components/ui/PrivateImage.tsx

import { useState, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import { patientApiService as apiService } from '@/services/api';
import { isDemoEnvironment } from '@/config/platforms';
import { cn } from '@/lib/utils';

// Cache for signed URLs to avoid repeated API calls
const urlCache = new Map<string, string>();

interface PrivateImageProps {
  attachment: {
    id: string | number;
    path?: string;
    url?: string;
    file_url?: string;
    filename?: string;
    file_name?: string;
    name?: string;
    mime_type?: string;
  };
  className?: string;
  onClick?: () => void;
  onError?: (error: any) => void;
  fallback?: React.ReactNode;
  [key: string]: any; // For other img props
}

export default function PrivateImage({ 
  attachment, 
  className, 
  onClick, 
  onError,
  fallback = null,
  ...props 
}: PrivateImageProps) {
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  
  useEffect(() => {
    let mounted = true;
    
    const loadImage = async () => {
      try {
        // Reset states
        setLoading(true);
        setError(false);
        
        // Check if we have a direct URL in demo environment
        if (isDemoEnvironment() && (attachment.url || attachment.file_url)) {
          const url = attachment.url || attachment.file_url;
          if (mounted) {
            setImageUrl(url);
            setLoading(false);
          }
          return;
        }
        
        // Check if we have a cached URL
        const cacheKey = attachment.path || String(attachment.id);
        if (cacheKey && urlCache.has(cacheKey)) {
          const cachedUrl = urlCache.get(cacheKey)!;
          if (mounted) {
            setImageUrl(cachedUrl);
            setLoading(false);
          }
          return;
        }
        
        // If we have a path, fetch the signed URL
        if (attachment.path) {
          const signedUrl = await apiService.getAttachmentDownloadUrl(attachment.path);
          
          // Cache the URL for 15 minutes (less than the 30-minute TTL)
          if (cacheKey) {
            urlCache.set(cacheKey, signedUrl);
            setTimeout(() => urlCache.delete(cacheKey), 15 * 60 * 1000);
          }
          
          if (mounted) {
            setImageUrl(signedUrl);
            setLoading(false);
          }
        } else if (attachment.url || attachment.file_url) {
          // Fallback to direct URL if no path
          const url = attachment.url || attachment.file_url;
          if (mounted) {
            setImageUrl(url);
            setLoading(false);
          }
        } else {
          // No valid source available
          throw new Error('No valid image source');
        }
      } catch (err) {
        console.error('Failed to load private image:', err);
        if (mounted) {
          setError(true);
          setLoading(false);
          onError?.(err);
        }
      }
    };
    
    loadImage();
    
    return () => {
      mounted = false;
    };
  }, [attachment, onError]);
  
  // Loading state
  if (loading) {
    return (
      <div className={cn(
        "flex items-center justify-center bg-gray-100 rounded-lg",
        className
      )}>
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }
  
  // Error state
  if (error || !imageUrl) {
    if (fallback) {
      return <>{fallback}</>;
    }
    
    return (
      <div className={cn(
        "flex items-center justify-center bg-gray-100 rounded-lg",
        className
      )}>
        <span className="text-gray-500 text-sm">Failed to load image</span>
      </div>
    );
  }
  
  return (
    <img
      src={imageUrl}
      alt={attachment.filename || attachment.name || 'Image'}
      className={className}
      onClick={onClick}
      onError={(e) => {
        setError(true);
        onError?.(e);
      }}
      {...props}
    />
  );
}

// Utility function to get signed URL for non-component usage (like markup tool)
export async function getSignedImageUrl(attachment: PrivateImageProps['attachment']): Promise<string> {
  // Check cache first
  const cacheKey = attachment.path || String(attachment.id);
  if (cacheKey && urlCache.has(cacheKey)) {
    return urlCache.get(cacheKey)!;
  }
  
  // In demo environment, use direct URL
  if (isDemoEnvironment() && (attachment.url || attachment.file_url)) {
    return attachment.url || attachment.file_url || '';
  }
  
  // Fetch signed URL
  if (attachment.path) {
    const signedUrl = await apiService.getAttachmentDownloadUrl(attachment.path);
    
    // Cache it
    if (cacheKey) {
      urlCache.set(cacheKey, signedUrl);
      setTimeout(() => urlCache.delete(cacheKey), 15 * 60 * 1000);
    }
    
    return signedUrl;
  }
  
  // Fallback to direct URL
  return attachment.url || attachment.file_url || '';
}